import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { calendarToday } from "../lib/days";
import { toReviewCount } from "../lib/reviewQueue";
import { unreadConversations } from "../lib/messages";
import type { Route } from "../lib/route";
import type { ShellCounts } from "../lib/nav";
import { useProposals, useRosterData } from "./useRosterData";
import { useReviewQueue } from "./useReviewQueue";

/* THE SIDEBAR'S TWO COUNTS (W81, the pro web step 1; W82 step 2) — the landing's TO-REVIEW count and the Inbox's unread.
 *
 * ⚠️ BOTH ARE READ THROUGH THE PAGES' OWN READERS, NEVER A SECOND RULE. To-review is `useReviewQueue` →
 * `reviewQueue.toReviewCount`, exactly what the Dashboard's tile and queue count (amendment (d); it was the needs-you
 * count in step 1, and one fixture must read one number on every route — Fable's step-1 ruling); unread is the
 * Inbox's own predicate — a message in one of THIS coach's threads, sent by the other party, with no `read_at` — counted
 * in CONVERSATIONS (2026-10-16, `unreadConversations`: the Inbox's `Unread` chip's number; it counted messages, 3 beside
 * the chip's 2): only `client_id` is read, so no message body leaves the database for a badge.
 *
 * ⚠️ WHEN THEY ARE RE-READ, AND THE COST SAID ONCE. The shell never remounts, so the roster is read once at sign-in and
 * again each time the coach ARRIVES on the landing (the page that shows the same count); the unread count on every
 * navigation, and when the Inbox says it stamped a thread read (`ht:inbox-read`). A failed read is `null` and draws no
 * badge — never a wrong number.
 */
export function useShellCounts(coachId: string, route: Route): ShellCounts {
  const [now] = useState(() => calendarToday());
  const [boardKey, setBoardKey] = useState(0);
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (route.name === "dashboard") setBoardKey((k) => k + 1);
  }, [route.name]);
  const state = useRosterData(boardKey, now);
  const data = state.status === "ready" ? state.data : null;
  const proposals = useProposals(data);
  const queue = useReviewQueue(data, proposals, now);
  const toReview = queue.status === "ready" ? toReviewCount(queue.queue) : null;

  const [unread, setUnread] = useState<number | null>(null);
  const [readSeq, setReadSeq] = useState(0);
  useEffect(() => {
    const bump = () => setReadSeq((n) => n + 1);
    window.addEventListener("ht:inbox-read", bump);
    return () => window.removeEventListener("ht:inbox-read", bump);
  }, []);
  const routeKey = route.name === "thread" ? `thread:${route.clientId}` : route.name;
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from("coach_messages")
        .select("client_id")
        .eq("coach_id", coachId)
        .neq("sender_id", coachId)
        .is("read_at", null);
      if (!cancelled) setUnread(error === null ? unreadConversations(data) : null);
    })();
    return () => { cancelled = true; };
  }, [coachId, routeKey, readSeq]);

  return { toReview, unread };
}
